<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';

import ChartBlock from '@/components/dashboard/ChartBlock.vue';
import LineYearChartBlock from '@/components/dashboard/LineYearChartBlock.vue';
import { useGlobalStore } from '@/stores/global';
import { useModuleStore } from '@/stores/module';
import { useOrganizationStore } from '@/stores/organization';
import type { Organization } from '@/types/organization';
import { getCurrentMonthName } from '@/utils/date';
import { computed, onMounted, provide, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const authStore = useAuthStore();
const moduleStore = useModuleStore();
const organizationStore = useOrganizationStore();
const globalStore = useGlobalStore();
const route = useRoute();

const organizationId = computed(() => {
    return route.params.organizationId ? route.params.organizationId.toString() : authStore.currentOrganization?._id;
});
provide('organizationId', organizationId);

const organization = ref<Organization | null>();

async function initialize() {
    await fetchOrganization();
}

onMounted(initialize);

watch(() => organizationId.value, initialize);

async function fetchOrganization(): Promise<void> {
    if (!organizationId.value) return;

    organization.value = await organizationStore.fetchById(organizationId.value);
    globalStore.pageTitle = `Dashboard ${organization.value?.name}`;
}
</script>

<template>
    <v-fade-transition>
        <div v-if="organization">
            <div>
                <v-row>
                    <v-col lg="4" md="6" sm="12" cols="12">
                        <ChartBlock
                            type="pie"
                            item-type="risk"
                            :is-completed="false"
                            title="Openstaand per risico"
                            empty-message="Momenteel geen openstaande actiepunten"
                            :key="organizationId"
                            class="mx-2"
                        />
                    </v-col>
                    <v-col lg="4" md="6" sm="12" cols="12">
                        <ChartBlock
                            type="pie"
                            item-type="risk"
                            :is-completed="true"
                            :title="`Afgerond in ${getCurrentMonthName().toLowerCase()} per risico`"
                            empty-message="Geen actiepunten afgerond deze maand"
                            :key="organizationId"
                            class="mx-2"
                        />
                    </v-col>
                    <v-col lg="4" md="6" sm="12" cols="12">
                        <LineYearChartBlock
                            title="Afgerond in"
                            :chart-options="{ plugins: { legend: { display: false } } }"
                            empty-message="Geen actiepunten afgerond in het geselecteerde jaar"
                            :key="organizationId"
                            class="mx-2"
                        />
                    </v-col>
                    <v-col lg="4" md="6" sm="12" cols="12" v-if="moduleStore.areMultipleModulesAccessible">
                        <ChartBlock
                            type="pie"
                            item-type="module"
                            :is-completed="false"
                            title="Openstaand per module"
                            empty-message="Momenteel geen openstaande actiepunten"
                            :key="organizationId"
                            class="mx-2"
                        />
                    </v-col>
                    <v-col lg="4" md="6" sm="12" cols="12" v-if="moduleStore.areMultipleModulesAccessible">
                        <ChartBlock
                            type="pie"
                            item-type="module"
                            :is-completed="true"
                            :title="`Afgerond in ${getCurrentMonthName().toLowerCase()} per module`"
                            empty-message="Momenteel geen afgeronde actiepunten"
                            :key="organizationId"
                            class="mx-2"
                        />
                    </v-col>
                    <v-col lg="4" md="6" sm="12" cols="12">
                        <ChartBlock
                            type="bar"
                            item-type="organization"
                            title="Openstaand per vestiging"
                            :is-completed="false"
                            :chart-options="{ plugins: { legend: { display: false } } }"
                            empty-message="Momenteel geen openstaande actiepunten"
                            :key="organizationId"
                            show-magnify
                            class="mx-2"
                        />
                    </v-col>
                    <v-col lg="4" md="6" sm="12" cols="12">
                        <ChartBlock
                            type="pie"
                            item-type="source"
                            title="Openstaand per bron"
                            :is-completed="false"
                            empty-message="Momenteel geen openstaande actiepunten"
                            :key="organizationId"
                            class="mx-2"
                        />
                    </v-col>
                    <v-col lg="4" md="6" sm="12" cols="12">
                        <ChartBlock
                            type="pie"
                            item-type="source"
                            :is-completed="true"
                            :title="`Afgerond in ${getCurrentMonthName().toLowerCase()} per bron`"
                            empty-message="Momenteel geen afgeronde actiepunten"
                            :key="organizationId"
                            class="mx-2"
                        />
                    </v-col>
                </v-row>
            </div>
        </div>
    </v-fade-transition>
</template>
